import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ModalShell, Field } from "../_dialog";
import { SignalGrid } from "./SignalGrid";
import { STATUS_LABEL } from "./constants";

export function ProspectDialog({ catalog, onClose, onSaved }) {
  const [form, setForm] = useState({
    clinic_name: "",
    website: "",
    city: "",
    state: "",
    clinic_type: "other",
    contact_name: "",
    contact_role: "",
    contact_email: "",
    phone: "",
    linkedin_url: "",
    source: "",
    notes: "",
    signal_ids: [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSignal = (sid) =>
    set("signal_ids", form.signal_ids.includes(sid) ? form.signal_ids.filter((x) => x !== sid) : [...form.signal_ids, sid]);

  const submit = async () => {
    if (!form.clinic_name.trim()) {
      toast.error("Clinic name is required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/opportunity/prospects", form);
      toast.success("Prospect added.");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title="New prospect"
      eyebrow="Opportunity Engine"
      onClose={onClose}
      testId="opp-create-dialog"
      size="lg"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-semibold rounded-full px-3 py-1.5 text-slate-600 hover:bg-slate-100"
            data-testid="opp-create-dialog-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            data-testid="opp-create-dialog-save"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Add prospect"}
          </button>
        </>
      }
    >
      <Field label="Clinic name">
        <input
          type="text"
          value={form.clinic_name}
          onChange={(e) => set("clinic_name", e.target.value)}
          autoFocus
          data-testid="opp-create-clinic-name"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Website">
          <input
            type="url"
            value={form.website}
            onChange={(e) => set("website", e.target.value)}
            placeholder="https://"
            data-testid="opp-create-website"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Clinic type">
          <select
            value={form.clinic_type}
            onChange={(e) => set("clinic_type", e.target.value)}
            data-testid="opp-create-clinic-type"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            {catalog.clinic_types.map((t) => (
              <option key={t} value={t}>{STATUS_LABEL(t)}</option>
            ))}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="City">
          <input type="text" value={form.city} onChange={(e) => set("city", e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        </Field>
        <Field label="State">
          <input type="text" value={form.state} onChange={(e) => set("state", e.target.value)} placeholder="CA" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm uppercase" />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Contact name">
          <input type="text" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        </Field>
        <Field label="Contact role">
          <input type="text" value={form.contact_role} onChange={(e) => set("contact_role", e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Contact email">
          <input type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        </Field>
        <Field label="Phone">
          <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        </Field>
      </div>
      <Field label="LinkedIn URL">
        <input type="url" value={form.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} placeholder="https://linkedin.com/…" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
      </Field>
      <Field label="Source">
        <input type="text" value={form.source} onChange={(e) => set("source", e.target.value)} placeholder="Where did you find them?" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
      </Field>
      <Field label="Initial signals (optional — also editable later)">
        <SignalGrid
          catalog={catalog}
          selected={form.signal_ids}
          onToggle={toggleSignal}
          testIdPrefix="opp-create-signal"
        />
      </Field>
      <Field label="Notes">
        <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
      </Field>
    </ModalShell>
  );
}

// ===========================================================================
// Signal toggle grid — reused in create + detail dialogs
